// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useQuery } from '@apollo/client';
import type { SelectChangeEvent } from '@mui/material/Select';
import { WEIGHT_UNITS, utils } from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Decimal from 'decimal.js';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { TitleCard } from '../components/common/card';
import EmptyContainer from '../components/common/empty/EmptyContainer';
import { PageError } from '../components/common/error';
import DateRangeContainer from '../components/common/filter/DateRangeContainer';
import FiltersContainer from '../components/common/filter/FiltersContainer';
import {
  ALL_ITEMS,
  getNewSelectedValues,
  makeDatePickerFilter,
  makeMultiSelectFilter,
} from '../components/common/filter/makeFilters';
import { PageLoading } from '../components/common/loading';
import AddPlantingDialog from '../components/planting/AddPlantingDialog';
import SurplusTable, { type SurplusGroup } from '../components/surplus/SurplusTable';
import { useGating, useUserContext } from '../context/UserContext';
import { Button, Typography } from '../lib/base';
import { AddIcon } from '../lib/icons';
import { SURPLUSES_BUFFERS_QUERY } from '../queries/SurplusesQueries';
import type { SurplusesHomeQuery, SurplusesHomeQueryVariables } from '../types/__generated__/graphql';
import { getID, getTimeRanges } from '../utils';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

interface PlantingData {
  readonly harvestTime: number;
  readonly expirationTime: number;
  readonly plantingId: string;
  readonly cropData: {
    readonly id: string;
    readonly crop_name: string;
    readonly yield_unit: string;
    readonly max_hold_time: number;
  };
  readonly surplusTrays: number;
  readonly yieldPerTrayOunces: number;
}

export default function Surpluses() {
  const { timezone, permissions } = useUserContext();
  const { canUseBasic } = useGating();
  const { todayStart } = getTimeRanges(timezone);

  const { data, loading, error } = useQuery<SurplusesHomeQuery, SurplusesHomeQueryVariables>(SURPLUSES_BUFFERS_QUERY, {
    variables: {
      timeAfter: todayStart,
    },
  });

  const userCanEditOrders = React.useMemo(() => permissions.can_edit_orders, [permissions]);
  const [newPlantingOpen, setNewPlantingOpen] = React.useState(false);
  const [cropFilter, setCropFilter] = React.useState<string[]>([ALL_ITEMS]);
  const [startDateFilter, setStartDateFilter] = React.useState<dayjs.Dayjs | null>(null);
  const [endDateFilter, setEndDateFilter] = React.useState<dayjs.Dayjs | null>(null);

  const onChangeCropFilter = React.useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const prevValues = [...cropFilter];
      const selectedValues = e.target.value as string[];
      const newSelectedValues = getNewSelectedValues(prevValues, selectedValues);
      setCropFilter(newSelectedValues);
    },
    [cropFilter],
  );
  const onChangeStartDate = React.useCallback(
    (newVal: dayjs.Dayjs | null) => {
      const newDateInTz = newVal?.tz(timezone, true).startOf('day') ?? null;
      setStartDateFilter(newDateInTz);
    },
    [timezone],
  );
  const onChangeEndDate = React.useCallback(
    (newVal: dayjs.Dayjs | null) => {
      const newDateInTz = newVal?.tz(timezone, true).endOf('day') ?? null;
      setEndDateFilter(newDateInTz);
    },
    [timezone],
  );

  // Combine plantings by crop, harvest time, and planted at time
  const allPlantings = React.useMemo(() => {
    return data?.surpluses?.edges.flatMap(({ node }) => node.plantings) ?? [];
  }, [data]);
  const allCrops = React.useMemo(() => {
    const seen = new Set<string>();
    const cropsToUse = allPlantings
      .map((p) => p.crop)
      .filter(utils.notNull)
      .filter((c) => {
        const id = getID(c.id);
        if (seen.has(id)) {
          return false;
        }
        seen.add(id);
        return true;
      });
    const options = cropsToUse.map((c) => ({ value: getID(c.id), label: c.crop_name }));
    options.sort((a, b) => a.label.localeCompare(b.label));

    return options;
  }, [allPlantings]);

  const groupedPlantings: readonly SurplusGroup[] = React.useMemo(() => {
    const groupedMap = allPlantings.reduce((acc, planting) => {
      if (planting.crop == null) {
        return acc;
      }
      const key = `${planting.planting_time}-${planting.planted_at ?? 'noplant'}-${planting.crop.id}`;
      const maybeExisting = acc.get(key);
      const maxHoldTime = planting.crop.max_hold_time ?? 0;
      if (maybeExisting === undefined) {
        acc.set(key, {
          plantingId: getID(planting.id),
          harvestTime: planting.planting_time,
          expirationTime: planting.planting_time + maxHoldTime,
          cropData: {
            ...planting.crop,
            max_hold_time: maxHoldTime,
          },
          surplusTrays: Number.parseFloat(planting.trays_rounded),
          yieldPerTrayOunces: Number.parseFloat(planting.yield_per_tray_ounces),
        });
      } else {
        acc.set(key, {
          ...maybeExisting,
          surplusTrays: Decimal.add(maybeExisting.surplusTrays, Number.parseFloat(planting.trays_rounded)).toNumber(),
        });
      }

      return acc;
    }, new Map<string, PlantingData>());

    // Add calculated surplus amount
    const calculatedSurpluses = Array.from(groupedMap.values())
      .map((group) => {
        const surplusAmountOunces = Decimal.mul(group.surplusTrays, group.yieldPerTrayOunces).toNumber();
        const convertedSurplusAmount = utils.round(
          utils.convert({
            inputUnit: WEIGHT_UNITS.OUNCES,
            outputUnit: group.cropData.yield_unit,
            quantity: surplusAmountOunces,
          }),
          group.cropData.yield_unit === WEIGHT_UNITS.OUNCES ? 1 : 0,
        );
        return {
          ...group,
          surplusAmount: convertedSurplusAmount,
        };
      })
      .filter((group) => group.surplusTrays > 0);

    // Sort by expiration time
    calculatedSurpluses.sort((a, b) => a.expirationTime - b.expirationTime);

    return calculatedSurpluses;
  }, [allPlantings]);
  const { minTime, maxTime } = React.useMemo(() => {
    const allTimes = groupedPlantings.map((p) => p.harvestTime).concat(groupedPlantings.map((p) => p.expirationTime));
    return {
      minTime: dayjs
        .unix(Math.min(...allTimes))
        .tz(timezone)
        .startOf('day'),
      maxTime: dayjs
        .unix(Math.max(...allTimes))
        .tz(timezone)
        .endOf('day'),
    };
  }, [groupedPlantings, timezone]);

  const cropFilterMenu = React.useMemo(() => {
    return makeMultiSelectFilter({
      value: cropFilter,
      onChangeValue: onChangeCropFilter,
      label: 'Crop Filter',
      id: 'crop-filter',
      options: allCrops,
    });
  }, [cropFilter, allCrops, onChangeCropFilter]);
  const startDateFilterMenu = React.useMemo(
    () =>
      makeDatePickerFilter({
        value: startDateFilter,
        onChangeValue: onChangeStartDate,
        maxDate: endDateFilter ?? undefined,
        minDate: minTime,
        label: 'After',
      }),
    [startDateFilter, onChangeStartDate, endDateFilter, minTime],
  );
  const endDateFilterMenu = React.useMemo(
    () =>
      makeDatePickerFilter({
        value: endDateFilter,
        onChangeValue: onChangeEndDate,
        minDate: startDateFilter ?? undefined,
        maxDate: maxTime,
        label: 'Before',
      }),
    [endDateFilter, onChangeEndDate, startDateFilter, maxTime],
  );
  const dateRange = React.useMemo(
    () => <DateRangeContainer startDateFilterMenu={startDateFilterMenu} endDateFilterMenu={endDateFilterMenu} />,
    [startDateFilterMenu, endDateFilterMenu],
  );
  const filtersRow = React.useMemo(
    () => <FiltersContainer dateRangeFilter={dateRange} otherFilters={[cropFilterMenu]} />,
    [cropFilterMenu, dateRange],
  );

  const filteredPlantings = React.useMemo(() => {
    return groupedPlantings.filter((group) => {
      const cropId = group.cropData.id;
      const filteredByCrop = cropFilter.includes(ALL_ITEMS) || cropFilter.includes(getID(cropId));
      const filteredByStartDate =
        startDateFilter === null ||
        group.harvestTime >= startDateFilter.unix() ||
        group.expirationTime >= startDateFilter.unix();
      const filteredByEndDate =
        endDateFilter === null ||
        group.harvestTime <= endDateFilter.unix() ||
        group.expirationTime <= endDateFilter.unix();

      return filteredByCrop && filteredByStartDate && filteredByEndDate;
    });
  }, [groupedPlantings, cropFilter, startDateFilter, endDateFilter]);

  const renderedContent = React.useMemo(() => {
    if (error) {
      return <PageError error={error} />;
    }
    if (loading) {
      return <PageLoading />;
    }
    if (filteredPlantings.length === 0) {
      return (
        <EmptyContainer
          title="You have no surpluses!"
          description="Surpluses come from cancelled or modified orders, or from trays being rounded up, and will show up here."
        />
      );
    }

    return <SurplusTable surpluses={filteredPlantings} />;
  }, [loading, error, filteredPlantings]);

  const options = React.useMemo(
    () =>
      userCanEditOrders ? (
        <Button
          id="new-order-button"
          variant="contained"
          onClick={() => setNewPlantingOpen(true)}
          endIcon={<AddIcon sx={{ color: 'secondary.contrastText' }} />}
          color="secondary"
          disabled={!canUseBasic}
        >
          <Typography variant="body1" sx={{ color: 'secondary.contrastText' }}>
            New Planting
          </Typography>
        </Button>
      ) : (
        <></>
      ),
    [userCanEditOrders, canUseBasic],
  );

  return (
    <>
      <Helmet>
        <title>{'Surpluses'}</title>
      </Helmet>
      <TitleCard title="Surpluses" rightElement={options} content={renderedContent} extraHeaderContent={filtersRow} />
      {newPlantingOpen && (
        <AddPlantingDialog
          isOpen={newPlantingOpen}
          onClose={() => setNewPlantingOpen(false)}
          onSuccess={() => {
            setNewPlantingOpen(false);
          }}
        />
      )}
    </>
  );
}
